import {
	Insight,
	InsightCacheKey,
	InsightGenerated,
	InsightPending,
	InsightStatus,
	KristaNarrativeStatus,
} from '../interfaces';

const CompleteInsightStatuses: InsightStatus[] = [
	'generated',
	'generated_with_caveat',
];

export function canonicalizeInsightCacheKey(key: InsightCacheKey): string {
	return JSON.stringify({
		calculationUpdatedAt: key.calculationUpdatedAt,
		calculationVersion: key.calculationVersion,
		currency: key.currency,
		kbVersion: key.kbVersion,
		product: key.product,
		promptVersion: key.promptVersion,
		targetId: key.targetId,
		targetType: key.targetType,
	});
}

export async function sha256Hex(value: string): Promise<string> {
	const encoded = new TextEncoder().encode(value);
	const digest = await globalThis.crypto.subtle.digest('SHA-256', encoded);
	return Array.from(new Uint8Array(digest))
		.map((byte) => byte.toString(16).padStart(2, '0'))
		.join('');
}

/**
 * Firestore document id for an insight. SHA-256 of the canonical cache key
 * so the same request always maps to the same document.
 */
export async function insightIdFromCacheKey(
	key: InsightCacheKey,
): Promise<string> {
	return sha256Hex(canonicalizeInsightCacheKey(key));
}

export function isCompleteInsight(
	insight: Insight | null | undefined,
): insight is InsightGenerated {
	return (
		insight != null &&
		CompleteInsightStatuses.includes(insight.status) &&
		insight.response != null
	);
}

export function isPendingInsight(
	insight: Insight | null | undefined,
): insight is Extract<Insight, { status: 'pending' }> {
	return insight != null && insight.status === 'pending';
}

/** Krista accepted the event, so a poll can read it. */
export function isPollableInsight(
	insight: Insight | null | undefined,
): insight is InsightPending & { eventId: string } {
	return isPendingInsight(insight) && !!insight.eventId;
}

export function insightStatusFromKrista(
	status: KristaNarrativeStatus,
): InsightStatus {
	switch (status) {
		case 'generated':
			return 'generated';
		case 'generated_with_caveat':
			return 'generated_with_caveat';
		case 'skipped':
		case 'skipped_immaterial':
		case 'declined':
			return 'failed';
	}
}
